import React, { useState } from 'react';
import { Dropdown } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import { useTheme } from '../../context/ThemeContext';
import { DateSelectorProps, DateOption, DateRange, DateOptionValue } from './types';
import { StyledButton, DropdownContainer, OptionList, OptionItem, ButtonText } from './styles';
import { PREDEFINED_DATE_OPTIONS, DEFAULT_DATE_OPTION_VALUE, DATE_RANGE_CALCULATORS } from './constants';

const DateSelector: React.FC<DateSelectorProps> = ({
  onDateSelect,
  defaultValue = DEFAULT_DATE_OPTION_VALUE,
  value: controlledValue,
}) => {
  const [selectedOption, setSelectedOption] = useState<DateOptionValue>(defaultValue);
  const [isOpen, setIsOpen] = useState(false);
  const { isDarkMode } = useTheme();
  const theme = { mode: (isDarkMode ? 'dark' : 'light') as 'light' | 'dark' };
  const currentValue = controlledValue !== undefined ? controlledValue : selectedOption;

  const getPredefinedDateRange = (value: DateOptionValue): DateRange => {
    const now = moment();
    const calculator = DATE_RANGE_CALCULATORS[value];
    return calculator ? calculator(now) : { startDate: null, endDate: null };
  };

  const handleOptionClick = (option: DateOption) => {
    if (controlledValue === undefined) {
      setSelectedOption(option.value);
    }
    setIsOpen(false);
    const selectedRange = getPredefinedDateRange(option.value);
    onDateSelect(selectedRange);
  };

  const selectedOptionLabel = PREDEFINED_DATE_OPTIONS.find((opt) => opt.value === currentValue)?.label || 'All Time';

  return (
    <Dropdown
      trigger={['click']}
      open={isOpen}
      onOpenChange={setIsOpen}
      dropdownRender={() => (
        <DropdownContainer theme={theme}>
          <OptionList>
            {PREDEFINED_DATE_OPTIONS.map((option) => (
              <OptionItem
                key={option.value}
                isSelected={currentValue === option.value}
                theme={theme}
                onClick={() => handleOptionClick(option)}
              >
                {option.label}
              </OptionItem>
            ))}
          </OptionList>
        </DropdownContainer>
      )}
    >
      <StyledButton theme={theme} onClick={(e) => e.preventDefault()}>
        <ButtonText theme={theme}>{selectedOptionLabel}</ButtonText>
        <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} />
      </StyledButton>
    </Dropdown>
  );
};

export default DateSelector;
